import React from 'react';
import { Box, Button, Card, LinearProgress, Skeleton, Typography } from '@mui/material';
import { Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UserQuota } from '../../types';

interface UsageQuotaCardProps {
  quota: UserQuota | null;
  loading: boolean;
}

export const UsageQuotaCard: React.FC<UsageQuotaCardProps> = ({ quota, loading }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const status = quota?.packageStatus ?? 'available';
  const color = status === 'empty' ? 'error' : status === 'low' ? 'warning' : 'primary';
  const packagePercentageUsed = quota?.percentageUsed ?? 0;
  // Advance = package advance allowance + purchased balance, shown as one bar.
  const advanceStatus = quota?.advanceTotalStatus ?? 'empty';
  const advanceColor =
    advanceStatus === 'empty' ? 'error' : advanceStatus === 'low' ? 'warning' : 'info';
  const advancePercentageUsed = quota?.advanceTotalPercentageUsed ?? 0;
  const advanceUsageHigh = advancePercentageUsed >= 90;
  const formattedResetDate = quota?.nextResetAt
    ? new Intl.DateTimeFormat(i18n.language === 'vi' ? 'vi-VN' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      }).format(new Date(quota.nextResetAt))
    : t('dashboard.stats.nextResetUnknown');

  return (
    <Card
      className="border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
      elevation={0}
    >
      <Box className="flex items-start justify-between">
        <Box className="flex-1">
          <Typography
            variant="body2"
            className="mb-1 font-medium text-gray-500 dark:text-slate-400"
          >
            {t('dashboard.stats.usageQuota')}
          </Typography>
          {loading && !quota ? (
            <UsageQuotaSkeleton />
          ) : (
            <>
              <Box className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                  {t('dashboard.stats.resetsOn', {
                    date: formattedResetDate,
                  })}
                </Typography>
                <Typography variant="h5" className="font-bold text-gray-900 dark:text-white">
                  {packagePercentageUsed}%
                </Typography>
              </Box>
              <Box className="mt-4">
                <LinearProgress
                  variant="determinate"
                  value={packagePercentageUsed}
                  color={color}
                  className="h-2 rounded-full"
                />
              </Box>
              <Box className="mt-4">
                <Box className="mb-2 flex items-center justify-between gap-3">
                  <Typography variant="body2" className="text-gray-600 dark:text-slate-300">
                    {quota?.canUseAdvanceFeatures
                      ? t('dashboard.stats.advanceAvailable')
                      : t('dashboard.stats.advanceEmpty')}
                  </Typography>
                  <Typography
                    variant="caption"
                    component={advanceUsageHigh ? 'button' : 'span'}
                    type={advanceUsageHigh ? 'button' : undefined}
                    onClick={advanceUsageHigh ? () => navigate('/subscription') : undefined}
                    className={
                      advanceUsageHigh
                        ? 'animate-pulse cursor-pointer border-0 bg-transparent p-0 font-semibold text-red-600 dark:text-red-400'
                        : 'text-gray-500 dark:text-slate-400'
                    }
                  >
                    {t('dashboard.stats.advancePercentUsed', {
                      percent: advancePercentageUsed,
                    })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={advancePercentageUsed}
                  color={advanceColor}
                  className="h-2 rounded-full"
                />
                <Button
                  size="small"
                  variant="outlined"
                  className="mt-3"
                  onClick={() => navigate('/subscription')}
                >
                  {t('dashboard.stats.topUp')}
                </Button>
              </Box>
              {status === 'empty' && (
                <Box className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate('/subscription')}
                  >
                    {t('dashboard.stats.upgradePlan')}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
        <Box className="ml-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <Activity className="text-blue-600 dark:text-blue-400" size={24} />
        </Box>
      </Box>
    </Card>
  );
};

const UsageQuotaSkeleton: React.FC = () => (
  <Box aria-hidden="true">
    <Skeleton variant="text" width="44%" height={36} />
    <Skeleton variant="rectangular" height={8} className="mt-4 rounded-full" />
    <Skeleton variant="text" width="62%" height={20} />
    <Skeleton variant="text" width="72%" height={20} className="mt-3" />
  </Box>
);
